import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { adaptEchartsOptions, SliderComponent, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { ScatterChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([ScatterChart, SVGRenderer, GridComponent, MarkLineComponent]);

const TEAL = '#14b8a6';
const _ROSE = '#f43f5e';
const TEAL_FADED = 'rgba(20, 184, 166, 0.5)';
const ROSE_FADED = 'rgba(244, 63, 94, 0.5)';

interface GameResult {
  netGain: number;
  jitter: number;
}

@Component({
  selector: 'dojo-fair-game-sim',
  standalone: true,
  imports: [NgxEchartsDirective, SliderComponent],
  template: `
    <div echarts class="fair-game-chart" [options]="adaptedOptions()" [initOpts]="initOpts" [autoResize]="true"></div>

    <div class="fgs-controls">
      <dojo-slider
        param="cost"
        [min]="0"
        [max]="1"
        [step]="0.01"
        [value]="cost()"
        [editable]="true"
        color="amber"
        (valueChange)="onCostChange($event)"
      />
      <button class="fgs-play-btn" (click)="play()" [disabled]="isPlaying()">
        @if (isPlaying()) {
          Playing...
        } @else {
          ▶ Play
        }
      </button>
    </div>

    <div class="fgs-stats">
      <div class="fgs-stat">
        <span class="fgs-stat-label">E(X) − c</span>
        <span class="fgs-stat-value fgs-stat-expected">{{ expectedNetGainDisplay() }}</span>
      </div>
      <div class="fgs-stat">
        <span class="fgs-stat-label">x̄ (observed)</span>
        <span class="fgs-stat-value fgs-stat-observed">{{ observedMeanDisplay() }}</span>
      </div>
      <div class="fgs-stat">
        <span class="fgs-stat-label">Result</span>
        <span class="fgs-stat-value">
          @if (isPlaying() || totalCount() === 0) {
            <span class="fgs-stat-idle">—</span>
          } @else if (verdict() === 'fair') {
            <span class="fgs-verdict-fair">Fair game</span>
          } @else if (verdict() === 'player') {
            <span class="fgs-verdict-player">Favours player</span>
          } @else {
            <span class="fgs-verdict-house">Favours house</span>
          }
        </span>
      </div>
    </div>

    @if (totalCount() > 0) {
      <div class="fgs-result">
        <span class="fgs-profit">{{ profitCount() }} wins</span>
        <span class="fgs-sep">/</span>
        <span class="fgs-loss">{{ lossCount() }} losses</span>
        <span class="fgs-sep">out of</span>
        <span class="fgs-total">{{ totalCount() }}</span>
      </div>
    }
  `,
  styleUrl: './fair-game-sim.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [provideEchartsCore({ echarts })],
  host: { class: 'block' },
})
export class FairGameSimComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });

    effect(
      () => {
        this.cost.set(this.initialCost());
      },
      { allowSignalWrites: true },
    );

    this.destroyRef.onDestroy(() => this.stopAnimation());
  }

  private readonly isDark;
  private readonly destroyRef = inject(DestroyRef);
  private animationTimer: ReturnType<typeof setInterval> | null = null;

  readonly initOpts = { renderer: 'svg' } as const;

  // ── Inputs ──
  readonly initialCost = input(0.5);
  readonly gameCount = input(1000);
  readonly batchSize = input(50);

  // ── State ──
  readonly cost = signal(0.5);
  readonly results = signal<GameResult[]>([]);
  readonly isPlaying = signal(false);

  // ── Computed ──
  readonly profitData = computed(() =>
    this.results()
      .filter((r) => r.netGain > 0)
      .map((r) => [r.netGain, r.jitter]),
  );

  readonly lossData = computed(() =>
    this.results()
      .filter((r) => r.netGain <= 0)
      .map((r) => [r.netGain, r.jitter]),
  );

  readonly profitCount = computed(() => this.profitData().length);
  readonly lossCount = computed(() => this.lossData().length);
  readonly totalCount = computed(() => this.results().length);
  readonly expectedNetGain = computed(() => +(0.5 - this.cost()).toFixed(4));

  readonly observedMean = computed(() => {
    const r = this.results();
    if (r.length === 0) {
      return 0;
    }
    return r.reduce((sum, g) => sum + g.netGain, 0) / r.length;
  });

  readonly expectedNetGainDisplay = computed(() => {
    const v = this.expectedNetGain();
    return `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
  });

  readonly observedMeanDisplay = computed(() => {
    if (this.totalCount() === 0) {
      return '—';
    }
    const v = this.observedMean();
    return `${v >= 0 ? '+' : ''}${v.toFixed(3)}`;
  });

  readonly verdict = computed(() => {
    const eng = this.expectedNetGain();
    if (Math.abs(eng) < 0.005) {
      return 'fair';
    }
    return eng > 0 ? 'player' : 'house';
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly chartOptions = computed<any>(() => {
    const profit = this.profitData();
    const loss = this.lossData();
    const eng = this.expectedNetGain();
    const mean = this.observedMean();
    const hasData = this.totalCount() > 0;

    return {
      animation: false,
      grid: { left: 40, right: 20, top: 15, bottom: 35 },
      xAxis: {
        type: 'value',
        min: -1.05,
        max: 1.05,
        interval: 0.25,
        axisLabel: { fontSize: 11, color: '#6b7280' },
        splitLine: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisTick: { lineStyle: { color: '#d1d5db' } },
        name: 'Net gain per game',
        nameLocation: 'middle',
        nameGap: 22,
        nameTextStyle: { fontSize: 11, color: '#9ca3af' },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 1,
        show: false,
      },
      series: [
        {
          type: 'scatter',
          data: profit,
          symbolSize: 3.5,
          itemStyle: { color: TEAL_FADED },
          silent: true,
        },
        {
          type: 'scatter',
          data: loss,
          symbolSize: 3.5,
          itemStyle: { color: ROSE_FADED },
          silent: true,
        },
        {
          type: 'scatter',
          data: [],
          silent: true,
          markLine: {
            silent: true,
            symbol: ['none', 'none'],
            data: [
              {
                xAxis: 0,
                lineStyle: { color: '#d1d5db', width: 1, type: 'dashed' },
                label: { show: false },
              },
              ...(hasData
                ? [
                    {
                      xAxis: eng,
                      lineStyle: { color: TEAL, width: 1.5, type: 'dotted' },
                      label: { show: false },
                    },
                    {
                      xAxis: mean,
                      lineStyle: { color: '#f59e0b', width: 2, type: 'solid' },
                      label: { show: false },
                    },
                  ]
                : []),
            ],
          },
        },
      ],
    };
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));

  // ── Actions ──
  readonly onCostChange = (value: number): void => {
    this.cost.set(value);
    this.stopAnimation();
    this.results.set([]);
  };

  play(): void {
    this.stopAnimation();
    this.results.set([]);
    this.isPlaying.set(true);

    const count = this.gameCount();
    const batch = this.batchSize();
    const cost = this.cost();

    const allResults: GameResult[] = [];
    for (let i = 0; i < count; i++) {
      allResults.push({
        netGain: Math.random() - cost,
        jitter: Math.random(),
      });
    }

    let added = 0;
    this.animationTimer = setInterval(() => {
      const nextBatch = allResults.slice(added, added + batch);
      added += nextBatch.length;
      this.results.update((prev) => [...prev, ...nextBatch]);

      if (added >= count) {
        this.stopAnimation();
      }
    }, 50);
  }

  private stopAnimation(): void {
    if (this.animationTimer !== null) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }
    this.isPlaying.set(false);
  }
}
